import { useState } from "react"

import { clsx } from "clsx"
import { Link } from "react-router-dom"

import {
  isRefreshPaymentRequiredError,
  REFRESH_PAYMENT_REQUIRED_MESSAGE,
} from "~/api/battery-financing-errors"
import type {
  BatteryFinancingSummaryDto,
  BatteryProposalDto,
  BatteryProposalFinancialSummaryDto,
} from "~/api/dto/battery-proposal.dto"
import useApplyBatteryCreditHumanApplication from "~/api/hooks/useApplyBatteryCreditHumanApplication"
import useBatteryProposalFinancialSummary from "~/api/hooks/useBatteryProposalFinancialSummary"
import useBatteryProposalFinancing from "~/api/hooks/useBatteryProposalFinancing"

import type {
  FinancialSummaryAction,
  FinancialSummaryApplicationAction,
} from "./financial-summary-action"
import {
  financialSummaryAction,
  financialSummaryFallbackAction,
} from "./financial-summary-action"
import {
  FinancialSummaryLedgerCard,
  SummarySlot,
} from "./financial-summary-card"
import { isBatteryProposalReadOnly } from "../battery-proposal-read-only"
import type { ResolvedContractAction } from "../contract/contract-action"
import {
  APPLICATION_STATUS_ACTION_LABEL,
  APPLY_FOR_FINANCING_LABEL,
} from "../financing-status/application-status-labels"
import type { BatteryDrawerLaunchState } from "../financing-status/battery-drawer-launch"
import { ConcertBatteryReserveNow } from "../financing-status/concert/ConcertBatteryReserveNow"
import { LightReachBatteryReserveNow } from "../financing-status/lightreach/LightReachBatteryReserveNow"
import { PricingOutOfDateIndicator } from "../pricing-out-of-date"
import { SelectLoanProductAction } from "../select-loan-product-action"
import {
  batteryContractPath,
  batteryFinancingStatusPath,
  batteryProposalPath,
} from "../setup/setup-steps"
import { SungageBatteryReserveNow } from "../sungage/SungageBatteryReserveNow"

export { SELECT_LOAN_PRODUCT_LABEL } from "../select-loan-product-action"
export {
  ENERGY_REBATES_DISCLOSURE,
  FinancialSummaryLedgerCard,
  financingTermsLabel,
  PAYMENT_FOOTNOTE_MARKER,
  SUMMARY_PAYMENT_OUT_OF_DATE_LABEL,
  SUMMARY_PAYMENT_UNAVAILABLE_PROMPT,
} from "./financial-summary-card"

export {
  APPLICATION_STATUS_ACTION_LABEL,
  applicationStatusLabel,
  APPLY_FOR_FINANCING_LABEL,
  CANCEL_REPLACE_APPLICATION_LABEL,
  CONFIRM_REPLACE_APPLICATION_LABEL,
  FINANCING_CHANGED_LABEL,
  MARK_APPROVED_CANCEL_LABEL,
  MARK_APPROVED_CONFIRM_LABEL,
  MARK_APPROVED_CONFIRM_PROMPT,
  MARK_APPROVED_LABEL,
  PREVIOUS_APPLICATIONS_HEADING,
  REOPEN_APPLICATION_LABEL,
  REPLACE_APPLICATION_LABEL,
} from "../financing-status/application-status-labels"

/**
 * Financial summary beneath the Investment cards. Renders the stored ledger —
 * badged while pricing is out of date — or a seller refresh notice when no
 * summary is stored. No client-side money math.
 */
export function FinancialSummary({
  proposal,
}: {
  proposal: BatteryProposalDto
}): JSX.Element | null {
  const hasStoredPricing = proposal.proposalCost != null
  const query = useBatteryProposalFinancialSummary(proposal.id, {
    enabled: hasStoredPricing,
  })
  // The Quote lives on the financing resource: one authority for payment,
  // Loan Product, and freshness. Reading it never recalculates.
  const financingQuery = useBatteryProposalFinancing(proposal.id, {
    enabled: hasStoredPricing,
  })
  if (!hasStoredPricing) {
    return null
  }

  // The decision lives in financialSummaryFallbackAction, beside its reasons.
  if (query.isError) {
    const action = financialSummaryFallbackAction({
      proposal,
      financing: financingQuery.data ?? null,
    })
    if (action.kind === `none`) {
      return null
    }
    return (
      <SummarySlot
        testId="battery-financial-summary-contract-fallback"
        className="print:hidden"
      >
        <div className="relative">
          <SummaryAction proposal={proposal} action={action} />
        </div>
      </SummarySlot>
    )
  }

  if (!query.data) {
    return (
      <div
        role="status"
        aria-label="Loading financial summary"
        data-testid="battery-financial-summary-skeleton"
        className="mt-3.5 min-h-[420px] animate-pulse rounded-lg border border-bp-border bg-bp-surface"
      />
    )
  }

  const { storedFinancialSummary: summary, pricingStatus } = query.data
  if (!summary) {
    // The server stores no cost (not_calculated): the render contract's
    // proposalCost was stale, and there is nothing to refresh or summarise.
    if (pricingStatus !== `out_of_date`) {
      return null
    }
    return <PricingOutOfDateNotice key={proposal.id} />
  }

  return (
    <FinancialSummaryCard
      key={proposal.id}
      summary={summary}
      financingSummary={query.data.financingSummary ?? null}
      proposal={proposal}
      staleProposalId={pricingStatus === `out_of_date` ? proposal.id : null}
      action={financialSummaryAction({
        proposal,
        financing: financingQuery.data ?? null,
      })}
    />
  )
}

/** Renders the one call to action the summary decided on beneath the ledger. */
function SummaryAction({
  proposal,
  action,
}: {
  proposal: BatteryProposalDto
  action: FinancialSummaryAction
}): JSX.Element | null {
  switch (action.kind) {
    case `concert`:
      return (
        <ConcertBatteryReserveNow
          proposal={proposal}
          quote={action.quote}
          needsLoanProduct={action.needsLoanProduct}
        />
      )
    case `sungage`:
      return (
        <SungageBatteryReserveNow
          proposal={proposal}
          quote={action.financing.quote}
          financing={action.financing}
          needsLoanProduct={action.needsLoanProduct}
        />
      )
    case `lightreach`:
      return (
        <LightReachBatteryReserveNow
          proposal={proposal}
          financing={action.financing}
          needsLoanProduct={action.needsLoanProduct}
        />
      )
    case `select-loan-product`:
      return <SelectLoanProductAction proposalId={proposal.id} />
    case `application-status`:
    case `apply`:
      return <ApplicationStatus proposalId={proposal.id} entry={action} />
    case `contract-reserve`:
      return (
        <ContractAction
          proposalId={proposal.id}
          action={action.contractAction}
        />
      )
    case `none`:
      return null
  }
}

/**
 * Inline Reserve now beneath the ledger. Contract status never renders here —
 * after an Existing Contract the Setup menu owns that entry.
 */
function ContractAction({
  proposalId,
  action,
}: {
  proposalId: string
  action: ResolvedContractAction
}): JSX.Element {
  return (
    <div className="mt-7 print:hidden">
      <Link
        to={batteryContractPath(proposalId)}
        data-testid="battery-financial-summary-contract-action"
        className={clsx(
          `font-display inline-flex items-center gap-2 rounded-full px-8 py-4 text-[length:var(--step-0)] font-bold focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none`,
          action.reserve
            ? `bg-white text-bp-accent shadow-[0_12px_32px_rgba(0,0,0,0.25)] hover:bg-white/90`
            : `border border-white/40 bg-white/10 text-white hover:bg-white/20`,
        )}
      >
        {action.label}
        <span aria-hidden="true">→</span>
      </Link>
    </div>
  )
}

/**
 * Credit Human application entry beneath the ledger. Before an Existing Loan
 * Application exists, Apply for financing stays here. After any application
 * attempt exists, only the white-pill Application status link remains — detail
 * and mutations live in the financing-status drawer.
 */
function ApplicationStatus({
  proposalId,
  entry,
}: {
  proposalId: string
  entry: FinancialSummaryApplicationAction
}): JSX.Element {
  if (entry.kind === `application-status`) {
    return (
      <div
        data-testid="battery-existing-loan-application"
        className="relative mt-6 print:hidden"
      >
        <Link
          to={batteryFinancingStatusPath(proposalId)}
          state={
            {
              returnTo: batteryProposalPath(proposalId),
            } satisfies BatteryDrawerLaunchState
          }
          data-testid="battery-financial-summary-application-status"
          className="font-display inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[length:var(--step-0)] font-bold text-bp-accent shadow-[0_12px_32px_rgba(0,0,0,0.25)] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
        >
          {APPLICATION_STATUS_ACTION_LABEL}
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    )
  }

  return (
    <div
      data-testid="battery-existing-loan-application"
      className="relative mt-6 print:hidden"
    >
      <ApplyForFinancingAction proposalId={proposalId} quoteId={entry.quoteId} />
    </div>
  )
}

/**
 * Open the handoff tab during the click gesture so transient user activation
 * is not lost while Apply awaits the network. Matches solar Credit Human handoff.
 */
function openHandoffDuringGesture(
  request: Promise<{ applicationUrl: string }>,
): Promise<void> {
  const applicationWindow = window.open(`about:blank`, `_blank`)
  if (applicationWindow) {
    applicationWindow.opener = null
  }

  return request
    .then(({ applicationUrl }) => {
      if (applicationWindow) {
        applicationWindow.location.href = applicationUrl
      } else {
        window.open(applicationUrl, `_blank`, `noopener,noreferrer`)
      }
    })
    .catch((error: unknown) => {
      applicationWindow?.close()
      throw error
    })
}

function ApplyForFinancingAction({
  proposalId,
  quoteId,
}: {
  proposalId: string
  quoteId: string
}): JSX.Element {
  const apply = useApplyBatteryCreditHumanApplication()
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  return (
    <>
      {apply.isError ? (
        <p role="alert" className="mb-3 text-[length:var(--step--2)] text-white">
          {isRefreshPaymentRequiredError(apply.error)
            ? REFRESH_PAYMENT_REQUIRED_MESSAGE
            : `Unable to open the Credit Human application`}
        </p>
      ) : null}
      <button
        type="button"
        disabled={apply.isLoading}
        onClick={() => {
          void openHandoffDuringGesture(
            apply.mutateAsync({
              proposalId,
              body: { idempotencyKey, quoteId },
            }),
          )
        }}
        className="font-display inline-flex cursor-pointer items-center gap-2 rounded-full bg-white px-8 py-4 text-[length:var(--step-0)] font-bold text-bp-accent shadow-[0_12px_32px_rgba(0,0,0,0.25)] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        {apply.isLoading ? `Opening application` : APPLY_FOR_FINANCING_LABEL}
        <span aria-hidden="true">→</span>
      </button>
    </>
  )
}

function PricingOutOfDateNotice(): JSX.Element {
  return (
    <SummarySlot className="print:hidden">
      <div className="relative">
        <PricingOutOfDateIndicator />
      </div>
    </SummarySlot>
  )
}

function FinancialSummaryCard({
  summary,
  financingSummary,
  proposal,
  staleProposalId,
  action,
}: {
  summary: BatteryProposalFinancialSummaryDto
  financingSummary: BatteryFinancingSummaryDto | null
  proposal: BatteryProposalDto
  staleProposalId: string | null
  action: FinancialSummaryAction
}): JSX.Element {
  const stale = staleProposalId != null
  // The seller's Updates panel names what's out of date, so their figures are
  // marked by an underline. A read-only viewer has no panel and keeps pills.
  const underlined = !isBatteryProposalReadOnly(proposal)
  return (
    <FinancialSummaryLedgerCard
      summary={summary}
      financingSummary={financingSummary}
      // A stale ledger stays visible on screen but never prints: a printed
      // brochure must not hand the homeowner superseded figures marked only
      // on screen.
      className={stale ? `print:hidden` : undefined}
      header={
        stale && !underlined ? (
          <div
            data-testid="battery-financial-summary-stale"
            className="relative mb-7 flex flex-wrap items-center gap-x-5 gap-y-3"
          >
            <PricingOutOfDateIndicator />
          </div>
        ) : null
      }
      staleMarks={underlined ? { pricing: stale } : undefined}
      actions={<SummaryAction proposal={proposal} action={action} />}
    />
  )
}
